import { type WindowService } from '@treasury/utils/services';
import { type ConfigurationService, Environment } from '../config';
import { type TmHttpClient } from '../http';
import { LoggingService } from '../logging';
import { LegacyRealtimeService } from './legacy-realtime.service';
import { ModernRealtimeService } from './modern-realtime.service';
import { NullRealtimeService } from './null-realtime.service';
import { IRealtimeService } from './realtime.service.types';

/**
 * Factory function for creating an instance of `IRealtimeService`
 * based on the current environment and desired implementation.
 */
export function createRealtimeService(
    useModern: boolean,
    services: {
        config: ConfigurationService;
        window: WindowService;
        http: TmHttpClient;
        logger: LoggingService;
    }
): IRealtimeService {
    const { config, window, http, logger } = services;
    const { environment } = config;
    const realtimeEnabled =
        environment !== Environment.Local && environment !== Environment.Unknown;

    if (!realtimeEnabled) {
        logger.logInfo('RealtimeService disabled in local/unknown environments.');
        return NullRealtimeService;
    }

    const realtimeService = useModern
        ? new ModernRealtimeService(config, http)
        : new LegacyRealtimeService(config, window);

    try {
        realtimeService.connected$.subscribe(isConnected => {
            const message = `SignalR connection ${isConnected ? 'established' : 'lost'}.`;
            logger.logInfo(message);
        });
        realtimeService.init(environment !== Environment.Production);
    } catch (e) {
        logger.logError('Failed connecting to SignalR', e as Error);
    }

    return realtimeService;
}
